'use client'

import * as React from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import type { DateRange } from 'react-day-picker'

import { useTranslation } from 'react-i18next'
import { cn } from "@/lib/utils"
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useIsMobile } from '@/hooks/use-mobile'

type PickerTimezone = 'Europe/Paris' | 'UTC'

const PARIS_TZ = 'Europe/Paris'

function getTzOffsetMs(date: Date, tz: string): number {
    const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' })
    const tzStr = date.toLocaleString('en-US', { timeZone: tz })
    return new Date(tzStr).getTime() - new Date(utcStr).getTime()
}

function getDateParts(date: Date, tz: PickerTimezone) {
    if (tz === 'UTC') {
        return {
            year: date.getUTCFullYear(),
            month: date.getUTCMonth(),
            day: date.getUTCDate(),
            dayOfWeek: date.getUTCDay(),
        }
    }
    const parts: Record<string, string> = {}
    new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        weekday: 'short', hour12: false,
    }).formatToParts(date).forEach(p => { parts[p.type] = p.value })

    const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    return {
        year: Number(parts.year),
        month: Number(parts.month) - 1,
        day: Number(parts.day),
        dayOfWeek: WEEKDAYS[parts.weekday] ?? 0,
    }
}

function toTzUTC(tz: PickerTimezone, year: number, month: number, day: number, h = 0, m = 0, s = 0, ms = 0): Date {
    if (tz === 'UTC') return new Date(Date.UTC(year, month, day, h, m, s, ms))
    const noonUTC = new Date(Date.UTC(year, month, day, 12, 0, 0))
    const offsetMs = getTzOffsetMs(noonUTC, tz)
    return new Date(Date.UTC(year, month, day, h, m, s, ms) - offsetMs)
}

function formatDateInTz(date: Date, tz: PickerTimezone, options?: Intl.DateTimeFormatOptions): string {
    return date.toLocaleDateString('en-US', { timeZone: tz, ...options })
}

// Keep Paris-specific exports for admin/analytics pages
export function formatParisDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    return formatDateInTz(date, PARIS_TZ, options)
}

export function getParisCurrentWeek(): DateRange {
    const { year, month, day, dayOfWeek } = getDateParts(new Date(), PARIS_TZ)
    const daysSinceMonday = (dayOfWeek + 6) % 7
    const mondayDay = day - daysSinceMonday
    const sundayDay = mondayDay + 6
    return {
        from: toTzUTC(PARIS_TZ, year, month, mondayDay),
        to: toTzUTC(PARIS_TZ, year, month, sundayDay, 23, 59, 59, 999),
    }
}

interface DateRangePickerProps {
    value: DateRange
    onChange: (range: DateRange) => void
    className?: string
    timezone?: PickerTimezone
}

function formatRange(range: DateRange | undefined, tz: PickerTimezone, fallback: string) {
    const { from, to } = range || {}
    if (!from || !to) return fallback
    try {
        const fmt = (d: Date) => formatDateInTz(d, tz, { month: 'short', day: 'numeric', year: 'numeric' })
        return `${fmt(from)} – ${fmt(to)}`
    } catch {
        return fallback
    }
}

export function DateRangePicker({ value, onChange, className, timezone = PARIS_TZ }: DateRangePickerProps) {
    const { t } = useTranslation('common')
    const isMobile = useIsMobile()
    const [open, setOpen] = React.useState(false)

    const applyPreset = (from: Date, to: Date) => {
        onChange({ from, to })
    }

    const { year, month, day, dayOfWeek } = getDateParts(new Date(), timezone)
    const daysSinceMonday = (dayOfWeek + 6) % 7

    const today = toTzUTC(timezone, year, month, day)
    const currentWeekMonday = toTzUTC(timezone, year, month, day - daysSinceMonday)
    const currentWeekSunday = toTzUTC(timezone, year, month, day - daysSinceMonday + 6, 23, 59, 59, 999)
    const lastWeekMonday = toTzUTC(timezone, year, month, day - daysSinceMonday - 7)
    const lastWeekSunday = toTzUTC(timezone, year, month, day - daysSinceMonday - 1, 23, 59, 59, 999)

    const presets = [
        { label: t('datePresets.currentWeek'), from: currentWeekMonday, to: currentWeekSunday },
        { label: t('datePresets.lastWeek'), from: lastWeekMonday, to: lastWeekSunday },
        { label: t('datePresets.last7Days'), from: toTzUTC(timezone, year, month, day - 6), to: today },
        { label: t('datePresets.last14Days'), from: toTzUTC(timezone, year, month, day - 13), to: today },
        { label: t('datePresets.last30Days'), from: toTzUTC(timezone, year, month, day - 29), to: today },
        { label: t('datePresets.last90Days'), from: toTzUTC(timezone, year, month, day - 89), to: today },
        { label: t('datePresets.last6Months'), from: toTzUTC(timezone, year, month, day - 179), to: today },
        { label: t('datePresets.lastYear'), from: toTzUTC(timezone, year, month, day - 364), to: today },
        { label: t('datePresets.thisMonth'), from: toTzUTC(timezone, year, month, 1), to: today },
        { label: t('datePresets.previousMonth'), from: toTzUTC(timezone, year, month - 1, 1), to: toTzUTC(timezone, year, month, 0, 23, 59, 59, 999) },
    ] as const

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn('h-9 w-full justify-start text-left font-normal sm:min-w-[260px] sm:w-auto', !value?.from && 'text-muted-foreground', className)}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formatRange(value, timezone, t('selectDateRange'))}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] p-0 sm:w-auto sm:max-w-none" align="start">
                <div className="flex flex-col items-stretch sm:flex-row sm:items-start">
                    <div className="border-b border-border p-3 sm:w-48 sm:border-b-0 sm:border-r">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-1">
                            {presets.map((p) => (
                                <Button
                                    key={p.label}
                                    variant="outline"
                                    size="sm"
                                    className="justify-start"
                                    onClick={() => applyPreset(p.from, p.to)}
                                >
                                    {p.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                    <div className="p-3">
                        <Calendar
                            mode="range"
                            numberOfMonths={isMobile ? 1 : 2}
                            defaultMonth={value?.from}
                            selected={value}
                            onSelect={(range) => {
                                if (!range) return
                                let from = range.from || value.from
                                let to = range.to || value.to
                                if (!from || !to) return
                                // Calendar returns local-tz dates; normalise to UTC midnight when in UTC mode
                                if (timezone === 'UTC') {
                                    from = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate()))
                                    to = new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()))
                                }
                                onChange({ from, to })
                            }}
                            disabled={{ after: new Date() }}
                            className="bg-transparent p-0"
                        />
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
